import React from 'react';
import { View, Pressable, StyleSheet, Image } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Text } from '../../../components/ui';
import { Theme } from '../../../utils/theme';

interface RecommendationPillProps {
  type: 'book' | 'insight';
  id: string; // bookId or insightId
  title: string;
  reason?: string;
  coverImageUrl?: string; // For book covers
  onPress: (type: 'book' | 'insight', id: string) => void;
}

const RecommendationPill: React.FC<RecommendationPillProps> = ({
  type,
  id,
  title,
  reason,
  coverImageUrl,
  onPress,
}) => {
  // Extract short name (everything before semicolon, or full title if no semicolon)
  const getShortTitle = (fullTitle: string): string => {
    const semicolonIndex = fullTitle.indexOf(';');
    if (semicolonIndex > 0) {
      return fullTitle.substring(0, semicolonIndex).trim();
    }
    return fullTitle;
  };

  const getIcon = () => {
    return type === 'book' ? 'book' : 'bulb';
  };

  const getIconColor = () => {
    return type === 'book' ? '#E67E22' : '#3498DB'; // Orange for books, blue for insights
  };

  const getBackgroundColor = () => {
    return type === 'book' ? 'rgba(230, 126, 34, 0.06)' : 'rgba(52, 152, 219, 0.06)';
  };

  const handlePress = () => {
    onPress(type, id);
  };

  const shortTitle = getShortTitle(title);

  return (
    <Pressable
      style={({ pressed }) => [
        styles.container,
        {
          backgroundColor: getBackgroundColor(),
          opacity: pressed ? 0.8 : 1,
          transform: pressed ? [{ scale: 0.98 }] : [{ scale: 1 }],
        }
      ]}
      onPress={handlePress}
    >
      <View style={styles.content}>
        <View style={styles.header}>
          {type === 'book' && coverImageUrl ? (
            <Image
              source={{ uri: coverImageUrl }}
              style={styles.bookCover}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.iconContainer, { backgroundColor: getIconColor() + '20' }]}>
              <Ionicons
                name={getIcon() as any}
                size={16}
                color={getIconColor()}
              />
            </View>
          )}
          <Text 
            variant="body2" 
            style={[styles.title, { color: getIconColor() }]} 
            numberOfLines={2}
          >
            {shortTitle}
          </Text>
          <Ionicons
            name="chevron-forward"
            size={16}
            color={getIconColor()}
            style={styles.chevron}
          />
        </View>
        
        {reason && (
          <Text variant="caption" color="secondary" style={styles.reason} numberOfLines={3}>
            {reason}
          </Text>
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: Theme.borderRadius.lg,
    marginVertical: Theme.spacing.xs,
    overflow: 'hidden',
    // Removed border completely for cleaner look
  },
  content: {
    padding: Theme.spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.sm,
    marginBottom: Theme.spacing.xs,
  },
  bookCover: {
    width: 32,
    height: 48,
    borderRadius: 6,
    marginTop: 2, // Slight offset for better alignment
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2, // Slight offset for better alignment
  },
  title: {
    flex: 1,
    fontFamily: Theme.typography.fontFamily.semiBold,
    fontSize: 15,
    lineHeight: 20,
  },
  chevron: {
    marginTop: 2, // Align with text
  },
  reason: {
    marginLeft: 40, // Align with title (cover/icon width + gap)
    lineHeight: 16,
    fontStyle: 'italic',
  },
});

export default RecommendationPill; 